import { PlayerSafetyException } from '@/player-safety-exception'
import { RequestException } from '@/request-exception'
import { mapToDateTime } from '@/utils/date-time-utils'
import {
  IMPERSONATION_READ_ONLY_CODE,
  IMPERSONATION_READ_ONLY_MESSAGE,
  showImpersonationReadOnlyToast,
} from '@/utils/impersonation-utils'
import { ValidationException } from '@/validation-exception'
import {
  type AfterFetchContext,
  type OnFetchErrorContext,
  type UseFetchReturn,
  useFetch,
  useUrlSearchParams,
} from '@vueuse/core'
import { useCookies } from '@vueuse/integrations/useCookies'

import { LeadSourceTracker } from './lead-source-tracking'
import { UUIDTracking } from './uuid-tracking'

type SetToMapOptions<T> = {
  key: keyof T | ((obj: T) => string)
  map: Map<string, T>
}

const AfterFetchParser = <T>(
  ctx: AfterFetchContext,
  setToMap?: SetToMapOptions<T>
) => {
  ctx.data = mapToDateTime(ctx.data)

  if (setToMap) {
    const data = ctx.data?.csi_content ?? ctx.data
    // check type and add to related store map
    if (typeof data === 'object') {
      let objects: Record<string, T>

      if (Object.prototype.hasOwnProperty.call(data, '__type')) {
        objects = { data }
      } else {
        objects = data
      }

      for (const value of Object.values(objects)) {
        const key =
          typeof setToMap.key === 'function'
            ? setToMap.key(value)
            : value[setToMap.key]

        setToMap.map.set(key as string, value)
      }
    }
  }

  return ctx
}

const OnFetchError = (ctx: OnFetchErrorContext) => {
  if (PlayerSafetyException.isPlayerSafetyError(ctx)) {
    ctx.error = PlayerSafetyException.fromFetchErrorContext(ctx)

    return ctx
  }

  let validationMessage: string | undefined
  const isImpersonationReadOnly =
    ctx.response?.status === 403 &&
    ctx.data?.code === IMPERSONATION_READ_ONLY_CODE

  if (isImpersonationReadOnly) {
    showImpersonationReadOnlyToast()
  }

  // Check if data is directly the errors object
  const errorData = ctx.data?.errors || ctx.data

  // Display the first error message from the error object
  if (errorData && typeof errorData === 'object') {
    const values = Object.values(errorData as Record<string, string[]>)
    if (values.length > 0 && Array.isArray(values[0]) && values[0].length > 0) {
      validationMessage = values[0][0]
    }
  }

  // Managing different error structures
  const objectError = Object.values(ctx.data?.errors ?? {})

  const errorMessage =
    (isImpersonationReadOnly ? IMPERSONATION_READ_ONLY_MESSAGE : null) ||
    validationMessage ||
    ctx.data?.error?.message ||
    ctx.data?.error ||
    ctx.data?.message ||
    ctx.response?.statusText ||
    (typeof ctx.data?.errors === 'string'
      ? ctx.data?.errors
      : Array.isArray(objectError)
        ? objectError[0]
        : objectError) ||
    ctx.error?.message

  if (ctx.response?.status === 422) {
    ctx.error = new ValidationException(
      {
        message: errorMessage ?? 'Validation Error',
        errors: errorData ?? ctx.data,
      },
      ctx.response
    )
  } else {
    ctx.error = new RequestException(errorMessage, ctx, ctx.response)
  }

  return ctx
}

/**
 * Determines whether credentials should be included in the request
 */
const shouldIncludeCredentials = (
  useCredentials: boolean,
  alwaysIncludeCredentials: boolean,
  authToken: string | undefined
): boolean => {
  return (
    alwaysIncludeCredentials ||
    import.meta.env.APP_ENV !== 'production' ||
    (useCredentials && authToken !== undefined)
  )
}

/**
 * A wrapper class around the browser implementation of Fetch.
 * This will set sensible default configurations with intelligent lead source tracking.
 *
 * Integrates with LeadSourceTracker for automatic source detection and attribution.
 * All POST requests automatically include source information for analytics.
 *
 * @see LeadSourceTracker for source detection logic and configuration
 * @see https://vueuse.org/core/useFetch/
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#canceling_a_request
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 */
export default class {
  /**
   * POSTs a JSON stringified data object.
   *
   * Supports multiple calling patterns for backward compatibility:
   * - post(url, body) - uses default credentials
   * - post(url, body, useCredentials) - controls credential usage
   * - post(url, body, useCredentials, alwaysIncludeCredentials) - forces credentials always
   * - post(url, body, useCredentials, setToMapOptions) - legacy format with options
   * - post(url, body, useCredentials, alwaysIncludeCredentials, setToMapOptions) - full control
   *
   * @param {string} url The API endpoint to send the data to.
   * @param {any} body The body of data to send to the API (uses `JSON.Stringify` to convert the body object).
   * @param {boolean} useCredentials Sets the credentials header which sends the auth cookie. Defaults to true.
   * @param {boolean|SetToMapOptions<T>} alwaysIncludeCredentialsOrSetToMap Forces sending credentials always, or legacy setToMapOptions object.
   * @param {SetToMapOptions<T>} setToMapOptions Set to automatically update a store map (when using 5-param format).
   * @throws {Error} Thrown when the server encountes an issue and unable to return a valid response.
   * @throws {ValidationException} Thrown when the passed data failed server-side validation (response code: 422).
   * @returns {Promise<T>} The response data will be parsed as JSON.
   */
  static async post<T = any>(
    url: string,
    body: any,
    useCredentials: boolean = true,
    alwaysIncludeCredentialsOrSetToMap?: boolean | SetToMapOptions<T>,
    setToMapOptions?: SetToMapOptions<T>
  ): Promise<UseFetchReturn<T>> {
    const utmParams = useUrlSearchParams()
    const cookies = useCookies(['authToken'], {
      autoUpdateDependencies: true,
    })
    const authToken = cookies.get('authToken')

    // Handle parameter overloading for backward compatibility
    let alwaysIncludeCredentials = false
    let finalSetToMapOptions = setToMapOptions

    if (alwaysIncludeCredentialsOrSetToMap !== undefined) {
      if (typeof alwaysIncludeCredentialsOrSetToMap === 'boolean') {
        alwaysIncludeCredentials = alwaysIncludeCredentialsOrSetToMap
      } else {
        finalSetToMapOptions = alwaysIncludeCredentialsOrSetToMap
      }
    }

    // Use credentials if not in production for Cloudfare Access
    const shouldIncludeCredentialsResult = shouldIncludeCredentials(
      useCredentials,
      alwaysIncludeCredentials,
      authToken
    )

    const mappedUtmParams = Object.fromEntries(
      Object.entries(utmParams).map(([key, value]) => {
        return [key.toLowerCase(), value]
      })
    )

    const postBody = {
      ...body,
      ...mappedUtmParams,
      source: LeadSourceTracker.determineSource(mappedUtmParams, body.source),
    }

    // Get UUID for fallback header (if cookie can't be sent for some reason)
    const uuid = UUIDTracking.get()

    return useFetch(
      url,
      {
        mode: 'cors',
        headers: {
          Accept: 'application/json', // receive JSON
          'Content-Type': 'application/json', // send JSON
          ...(useCredentials && authToken
            ? {
                Authorization: 'Bearer ' + authToken,
              }
            : {}),
          ...(uuid
            ? {
                'X-GL-UUID': uuid,
              }
            : {}),
        },
        credentials: shouldIncludeCredentialsResult ? 'include' : 'omit',
      },
      {
        timeout: 60000, // 1 minute
        updateDataOnError: true,
        refetch: true,
        afterFetch: (ctx: AfterFetchContext) =>
          AfterFetchParser(ctx, finalSetToMapOptions),
        onFetchError(ctx: OnFetchErrorContext) {
          console.error('post::onFetchError', {
            status: ctx.response?.status,
            data: ctx.data,
          })

          return OnFetchError(ctx)
        },
      }
    )
      .post(postBody)
      .json()
  }

  /**
   * Sends a data object via a GET request.
   * The data needs to be stringified.
   *
   * Supports multiple calling patterns for backward compatibility:
   * - get(url) - uses default credentials
   * - get(url, useCredentials) - controls credential usage
   * - get(url, useCredentials, setToMapOptions) - legacy format with options
   * - get(url, useCredentials, alwaysIncludeCredentials) - forces credentials always
   * - get(url, useCredentials, alwaysIncludeCredentials, setToMapOptions) - full control
   *
   * @param {string} url The API endpoint to send the data to.
   * @param {boolean} useCredentials Sets the credentials header which sends the auth cookie. Defaults to true.
   * @param {boolean|SetToMapOptions<T>} alwaysIncludeCredentialsOrSetToMap Forces sending credentials always, or legacy setToMapOptions object.
   * @param {SetToMapOptions<T>} setToMapOptions Set to automatically update a store map (when using 4-param format).
   * @throws {Error} Thrown when the server encountes an issue and unable to return a valid response.
   * @throws {ValidationException} Thrown when the passed data failed server-side validation (response code: 422).
   * @returns {Promise<T>} The response data will be parsed as JSON.
   */
  static async get<T = any>(
    url: string,
    useCredentials: boolean = true,
    alwaysIncludeCredentialsOrSetToMap?: boolean | SetToMapOptions<T>,
    setToMapOptions?: SetToMapOptions<T>
  ): Promise<UseFetchReturn<T>> {
    const cookies = useCookies(['authToken'], {
      autoUpdateDependencies: true,
    })
    const authToken = cookies.get('authToken')

    // Handle parameter overloading for backward compatibility
    let alwaysIncludeCredentials = false
    let finalSetToMapOptions = setToMapOptions

    if (alwaysIncludeCredentialsOrSetToMap !== undefined) {
      if (typeof alwaysIncludeCredentialsOrSetToMap === 'boolean') {
        alwaysIncludeCredentials = alwaysIncludeCredentialsOrSetToMap
      } else {
        finalSetToMapOptions = alwaysIncludeCredentialsOrSetToMap
      }
    }

    // Use credentials if not in production for Cloudfare Access
    const shouldIncludeCredentialsResult = shouldIncludeCredentials(
      useCredentials,
      alwaysIncludeCredentials,
      authToken
    )

    // Get UUID for fallback header (if cookie can't be sent for some reason)
    const uuid = UUIDTracking.get()

    return useFetch(
      url,
      {
        mode: 'cors',
        headers: {
          Accept: 'application/json', // receive JSON
          'Content-Type': 'application/json', // send JSON
          ...(useCredentials && authToken
            ? {
                Authorization: 'Bearer ' + authToken,
              }
            : {}),
          ...(uuid
            ? {
                'X-GL-UUID': uuid,
              }
            : {}),
        },
        credentials: shouldIncludeCredentialsResult ? 'include' : 'omit',
      },
      {
        timeout: 60000, // 1 minute
        updateDataOnError: true,
        refetch: true,
        afterFetch: (ctx: AfterFetchContext) =>
          AfterFetchParser(ctx, finalSetToMapOptions),
        onFetchError(ctx: OnFetchErrorContext) {
          console.error('get::onFetchError', {
            status: ctx.response?.status,
            data: ctx.data,
          })

          return OnFetchError(ctx)
        },
      }
    )
      .get()
      .json()
  }
}
